import { useCallback } from 'react';

import { useMixpanel } from 'context/mixpanel-context';
import { Package, SalesPortalCheckoutResponse } from 'types';

export type SalesPortalSurface = 'public' | 'athlete_app';

export interface TrackingEventProperties {
  /**
   * Track invoice collected by the staff and also athlete.
   * Used by both staff and athletes when collecting/purchasing invoices.
   */
  invoice_collected?: {
    invoice_id: string;
    athlete_id: string;
    amount_collected: number;
    payment_method: string;
    previous_status: string;
    new_status: string;
  };

  /**
   * Track report generated by the staff and also athlete.
   * Used by both staff and athletes when generating/exporting reports.
   */
  report_generated?: {
    report_type: string;
    start_date: string | null;
    end_date: string | null;
  };

  /**
   * Track document signing triggered by the athlete and also the staff.
   * Used by both staff and athletes when signing documents.
   */
  document_sign?: {
    document_id: string;
    document_name: string;
  };

  /**
   * Track discount code redemption via the code-input field. Fired by both
   * the athlete app (authenticated) and the public sales portal (no session).
   */
  discount_code_applied?: {
    discount_id: string;
    discount_name: string;
    discount_code: string;
    value_type: 'PERCENTAGE' | 'FIXED_AMOUNT';
    applied_to: 'athlete_app' | 'sales_portal';
  };

  /**
   * Track completion of a public sales-portal checkout accordion step. Portal
   * visitors are usually anonymous (no session base props), so gym_id is passed
   * explicitly.
   */
  sales_portal_checkout_step_completed?: {
    step: string;
    gym_id: string;
    package_id: string;
  };

  /**
   * Track a completed public sales-portal purchase. Fired after the checkout
   * POST succeeds; gym_id is explicit because portal sessions carry no gym.
   * reservation_outcome / confirmation_variant describe the pre-selected-class
   * result and which confirmation screen the buyer saw (spec Item 1).
   */
  sales_portal_purchase_completed?: {
    gym_id: string;
    package_id: string;
    package_type: Package['type'];
    billing_plan_id: string;
    number_of_installments: number;
    portal: SalesPortalSurface;
    reservation_outcome: SalesPortalCheckoutResponse['reservationOutcome'] | null;
    confirmation_variant: string;
  };

  /**
   * Track an athlete opening a package from the sales-portal catalog, public or athlete app.
   */
  sales_portal_package_viewed?: {
    gym_id: string;
    package_id: string;
    package_type: Package['type'];
    portal: SalesPortalSurface;
  };

  /**
   * Track an athlete reaching the billing plan step of a sales-portal checkout, once per plan.
   */
  sales_portal_purchase_started?: {
    gym_id: string;
    package_id: string;
    package_type: Package['type'];
    billing_plan_id: string;
    number_of_installments: number;
    portal: SalesPortalSurface;
  };

  /**
   * Track a buyer resuming an abandoned checkout — same session (the welcome-back
   * banner) or from the recovery email's magic link (spec Item 11). gym_id is
   * explicit because portal sessions carry no gym.
   */
  sales_portal_checkout_resumed?: {
    gym_id: string;
    package_id: string;
    source: 'session' | 'email';
  };

  /** Fired per toggle whose value changed when the settings card is saved. */
  reservation_limit_setting_changed?: {
    event_kind: 'classes' | 'appointments';
    enabled: boolean;
  };
}

export const useTracking = (): any => {
  const { track } = useMixpanel();

  const trackInvoiceCollected = useCallback(
    (
      invoiceId: string,
      athleteId: string,
      amountCollected: number,
      paymentMethod: string,
      previousStatus: string,
      newStatus: string,
    ) => {
      track('invoice_collected', {
        invoice_collected: {
          invoice_id: invoiceId,
          athlete_id: athleteId,
          amount_collected: amountCollected,
          payment_method: paymentMethod,
          previous_status: previousStatus,
          new_status: newStatus,
        },
      });
    },
    [track],
  );

  const trackReportGenerated = useCallback(
    (reportType: string, startDate: string | null, endDate: string | null) => {
      track('report_generated', {
        report_generated: {
          report_type: reportType,
          start_date: startDate,
          end_date: endDate,
        },
      });
    },
    [track],
  );

  const trackDocumentSign = useCallback(
    (documentId: string, documentName: string) => {
      track('document_sign', {
        document_sign: {
          document_id: documentId,
          document_name: documentName,
        },
      });
    },
    [track],
  );

  const trackDiscountCodeApplied = useCallback(
    (
      discountId: string,
      discountName: string,
      discountCode: string,
      valueType: 'PERCENTAGE' | 'FIXED_AMOUNT',
      appliedTo: 'athlete_app' | 'sales_portal',
    ) => {
      track('discount_code_applied', {
        discount_code_applied: {
          discount_id: discountId,
          discount_name: discountName,
          discount_code: discountCode,
          value_type: valueType,
          applied_to: appliedTo,
        },
      });
    },
    [track],
  );

  const trackSalesPortalCheckoutStepCompleted = useCallback(
    (step: string, gymId: string, packageId: string) => {
      track('sales_portal_checkout_step_completed', {
        sales_portal_checkout_step_completed: {
          step,
          gym_id: gymId,
          package_id: packageId,
        },
      });
    },
    [track],
  );

  const trackSalesPortalPurchaseCompleted = useCallback(
    (
      gymId: string,
      packageId: string,
      packageType: Package['type'],
      billingPlanId: string,
      numberOfInstallments: number,
      portal: SalesPortalSurface,
      reservationOutcome: SalesPortalCheckoutResponse['reservationOutcome'] | null,
      confirmationVariant: string,
    ) => {
      track('sales_portal_purchase_completed', {
        sales_portal_purchase_completed: {
          gym_id: gymId,
          package_id: packageId,
          package_type: packageType,
          billing_plan_id: billingPlanId,
          number_of_installments: numberOfInstallments,
          portal,
          reservation_outcome: reservationOutcome,
          confirmation_variant: confirmationVariant,
        },
      });
    },
    [track],
  );

  const trackSalesPortalPackageViewed = useCallback(
    (gymId: string, packageId: string, packageType: Package['type'], portal: SalesPortalSurface) => {
      track('sales_portal_package_viewed', {
        sales_portal_package_viewed: {
          gym_id: gymId,
          package_id: packageId,
          package_type: packageType,
          portal,
        },
      });
    },
    [track],
  );

  const trackSalesPortalPurchaseStarted = useCallback(
    (
      gymId: string,
      packageId: string,
      packageType: Package['type'],
      billingPlanId: string,
      numberOfInstallments: number,
      portal: SalesPortalSurface,
    ) => {
      track('sales_portal_purchase_started', {
        sales_portal_purchase_started: {
          gym_id: gymId,
          package_id: packageId,
          package_type: packageType,
          billing_plan_id: billingPlanId,
          number_of_installments: numberOfInstallments,
          portal,
        },
      });
    },
    [track],
  );

  const trackSalesPortalCheckoutResumed = useCallback(
    (gymId: string, packageId: string, source: 'session' | 'email') => {
      track('sales_portal_checkout_resumed', {
        sales_portal_checkout_resumed: {
          gym_id: gymId,
          package_id: packageId,
          source,
        },
      });
    },
    [track],
  );

  const trackReservationLimitSettingChanged = useCallback(
    (eventKind: 'classes' | 'appointments', enabled: boolean) => {
      track('reservation_limit_setting_changed', {
        reservation_limit_setting_changed: {
          event_kind: eventKind,
          enabled,
        },
      });
    },
    [track],
  );

  return {
    trackInvoiceCollected,
    trackReportGenerated,
    trackDocumentSign,
    trackDiscountCodeApplied,
    trackSalesPortalCheckoutStepCompleted,
    trackSalesPortalPurchaseCompleted,
    trackSalesPortalPackageViewed,
    trackSalesPortalPurchaseStarted,
    trackSalesPortalCheckoutResumed,
    trackReservationLimitSettingChanged,
  };
};
